import { useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { X, Plus } from "lucide-react";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { parseLocalDate, todayLocal, compare } from "./dates";
import { jobDuration, totalExperience, decimalYears, employmentGaps, gapLabel, type JobSpan } from "./experience";

const TOOL_INFO_CONTENT = {
  what: `Calculates work experience duration in years, months, and days between a start and end date — add multiple jobs to also see your total combined professional experience across all of them, useful for resumes, LinkedIn, or job applications that ask for total years of experience.`,
  how: `Enter a start date and end date for each job (or check "Currently working here" to use today's date), and add as many jobs as you need. Each one shows its own duration, and the total combines them all. With two or more jobs, any gap between them is listed too (for example "Gap: 2 mo"), which is the follow-up many application forms and recruiters ask about.`,
  tip: `Overlapping jobs (for example a freelance job alongside a full-time role) are merged first, so the overlapping period is counted once. Month convention: months are calendar months counted from the start date (31 Jan to 28 Feb is 0 months, 28 days); each period's months and days are then added up, and a 30-day month is used only when carrying leftover days between periods. Tick or untick "Count the end date as a full day": HR and visa forms usually expect 1 January to 31 December to be one year.`,
};

interface JobEntry {
  id: number;
  company: string;
  start: string;
  end: string;
  current: boolean;
}

let nextId = 1;

const pad = (n: number) => String(n).padStart(2, "0");
const isoOf = (d: { y: number; m: number; d: number }) => `${d.y}-${pad(d.m)}-${pad(d.d)}`;
const isoToday = () => { const t = todayLocal(); return `${t.y}-${pad(t.m)}-${pad(t.d)}`; };

export default function JobExperienceCalculator() {
  const today = isoToday();
  const [inclusive, setInclusive] = useState(true);
  const [jobs, setJobs] = useState<JobEntry[]>([
    { id: 0, company: "", start: "", end: today, current: true },
  ]);

  const addJob = () => {
    setJobs(prev => [...prev, { id: nextId++, company: "", start: "", end: today, current: true }]);
  };

  const removeJob = (id: number) => {
    setJobs(prev => prev.filter(j => j.id !== id));
  };

  const updateJob = (id: number, field: keyof JobEntry, value: string | boolean) => {
    setJobs(prev => prev.map(j => j.id === id ? { ...j, [field]: value } : j));
  };

  const spanOf = (j: JobEntry): JobSpan | null => {
    const st = parseLocalDate(j.start), en = parseLocalDate(j.current ? today : j.end);
    return st && en ? { start: st, end: en } : null;
  };
  const results = jobs.map(j => { const sp = spanOf(j); return { job: j, duration: sp ? jobDuration(sp, inclusive) : null }; });
  const validResults = results.filter(r => r.duration !== null);
  const total = validResults.length > 0 ? totalExperience(jobs.map(spanOf).filter((x): x is JobSpan => !!x), inclusive) : null;
  const gaps = employmentGaps(jobs.map(spanOf).filter((x): x is JobSpan => !!x), inclusive);
  const copySummary = () => {
    if (!total) return;
    const lines = results.filter(r => r.duration).map(r => `${r.job.company || "Job"}: ${r.job.start} to ${r.job.current ? "present" : r.job.end} = ${r.duration!.years} y ${r.duration!.months} m ${r.duration!.days} d`);
    for (const g of gaps) lines.push(`Gap: ${gapLabel(g)} between ${isoOf(g.after)} and ${isoOf(g.before)}`);
    lines.push(`Total (overlaps counted once): ${total.years} years ${total.months} months ${total.days} days (about ${decimalYears(total.totalDays)} ${decimalYears(total.totalDays) === 1 ? "year" : "years"})`);
    navigator.clipboard?.writeText(lines.join("\n")).catch(() => {});
  };

  const { blocked } = useLogToolUsage(true, 3000, { ownActions: true });


  return (
    <ToolContainer>
      <div className="max-w-2xl mx-auto space-y-4">
        {jobs.map((job, i) => {
          const result = results.find(r => r.job.id === job.id)?.duration;
          return (
            <div key={job.id} className="p-4 rounded-lg border bg-card space-y-3">
              <div className="flex items-center justify-between">
                <Input
                  aria-label={`Job name, Job ${i + 1}`}
                  value={job.company}
                  onChange={e => updateJob(job.id, "company", e.target.value)}
                  placeholder={`Job ${i + 1} (e.g. company or role name, optional)`}
                  className="border-none px-0 h-auto text-sm font-medium focus-visible:ring-0"
                />
                {jobs.length > 1 && (
                  <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" aria-label={`Remove Job ${i + 1}`} onClick={() => removeJob(job.id)}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label htmlFor={`je-start-${job.id}`} className="text-xs">Start Date</Label><Input id={`je-start-${job.id}`} aria-label={`Start date, Job ${i + 1}`} type="date" value={job.start} onChange={e => updateJob(job.id, "start", e.target.value)} /></div>
                <div>
                  <Label htmlFor={`je-end-${job.id}`} className="text-xs">End Date</Label>
                  <Input id={`je-end-${job.id}`} aria-label={`End date, Job ${i + 1}`} type="date" value={job.end} onChange={e => updateJob(job.id, "end", e.target.value)} disabled={job.current} />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id={`current-${job.id}`} checked={job.current} onCheckedChange={(v) => updateJob(job.id, "current", !!v)} />
                <Label htmlFor={`current-${job.id}`} className="text-xs font-normal cursor-pointer">Currently working here</Label>
              </div>
              {result && !blocked && (
                <p className="text-sm font-medium text-primary pt-1">
                  {result.years} year{result.years !== 1 ? "s" : ""}, {result.months} month{result.months !== 1 ? "s" : ""}, {result.days} day{result.days !== 1 ? "s" : ""}
                  <span className="text-muted-foreground font-normal"> ({result.totalDays.toLocaleString()} total days)</span>
                </p>
              )}
              {!job.current && !!parseLocalDate(job.start) && !!parseLocalDate(job.end) && compare(parseLocalDate(job.end)!, parseLocalDate(job.start)!) < 0 && (
                <p role="alert" data-testid="je-reversed" className="text-xs text-destructive">End date is before the start date, so this job isn't counted. Swap the dates or correct one of them.</p>
              )}
            </div>
          );
        })}

        <Button variant="outline" size="sm" onClick={addJob} className="gap-1.5">
          <Plus className="h-3.5 w-3.5" /> Add Another Job
        </Button>

        <label className="flex items-center gap-2 text-sm"><Checkbox aria-label="Count the end date as a full day" checked={inclusive} onCheckedChange={(v) => setInclusive(!!v)} /> Count the end date as a full day (1 Jan to 31 Dec = 1 year)</label>

        {total && !blocked && (
          <div className="p-6 rounded-xl border bg-primary/5 text-center" data-testid="je-total">
            <p className="text-xs text-muted-foreground mb-1">Total experience ({validResults.length} job{validResults.length === 1 ? "" : "s"}, overlapping periods counted once)</p>
            <p className="text-3xl font-bold text-primary">
              {total.years} yr {total.months} mo {total.days} d
            </p>
            <p className="text-sm text-muted-foreground mt-1">about {decimalYears(total.totalDays)} {decimalYears(total.totalDays) === 1 ? "year" : "years"}</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={copySummary}>Copy summary</Button>
          </div>
        )}
        {total && !blocked && validResults.length >= 2 && (
          <div className="p-4 rounded-lg border bg-card" data-testid="je-gaps">
            <p className="text-sm font-medium mb-1">Gaps between jobs</p>
            {gaps.length === 0
              ? <p className="text-sm text-muted-foreground">No gaps: the jobs you entered run back to back or overlap.</p>
              : <ul className="text-sm space-y-1">{gaps.map((g, i) => <li key={i}>Gap: <strong>{gapLabel(g)}</strong> <span className="text-muted-foreground">({g.totalDays.toLocaleString()} days, between {isoOf(g.after)} and {isoOf(g.before)})</span></li>)}</ul>}
          </div>
        )}
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
